import { AssetClassCode } from '@/types/assets';
import { supabase } from '../SupabaseClient/supabase-client';

export type AssetClassRecord = {
  id: string; // id da tabela
  code: AssetClassCode; // slug da classe (stocks, fii, etc)
  percentage: number;
};

const TABLE = 'asset_classes';

const roundPercentage = (value: number, precision = 2) => {
  const factor = 10 ** precision;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

const toUiPercentage = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(value ?? 0);
  if (!Number.isFinite(parsed)) return 0;
  const nonNegative = Math.max(0, parsed);
  const normalized = nonNegative <= 1 ? nonNegative * 100 : nonNegative;
  return roundPercentage(normalized);
};

const toPersistedPercentage = (value: number) => {
  const parsed = Number.isFinite(value) ? value : 0;
  const nonNegative = Math.max(0, parsed);
  const normalized = nonNegative > 1 ? nonNegative / 100 : nonNegative;
  return Math.min(1, roundPercentage(normalized, 4));
};

const mapRow = (
  row: { id?: string | number; code?: string; percentage?: number } | null,
): AssetClassRecord | null => {
  if (!row || typeof row.code !== 'string') {
    return null;
  }

  const rowId =
    typeof row.id === 'string' ? row.id : row?.id !== undefined ? String(row.id) : '';
  if (!rowId) return null;

  const percentageValue =
    typeof row.percentage === 'number' ? row.percentage : Number(row.percentage ?? 0);

  return {
    id: rowId,
    code: row.code as AssetClassCode,
    percentage: toUiPercentage(percentageValue),
  };
};

let cachedUserId: string | null = null;

supabase.auth.onAuthStateChange((_event, session) => {
  cachedUserId = session?.user?.id ?? null;
});

const getUserId = async (): Promise<string> => {
  if (cachedUserId) return cachedUserId;

  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) {
    cachedUserId = null;
    throw new Error('Usuário não autenticado');
  }

  cachedUserId = data.user.id;
  return cachedUserId;
};

const UPDATE_DEBOUNCE_MS = 400;

type PendingUpdate = {
  timer: ReturnType<typeof setTimeout>;
  record: Pick<AssetClassRecord, 'id' | 'percentage'>;
  resolvers: Array<{
    resolve: (value: AssetClassRecord) => void;
    reject: (reason?: unknown) => void;
  }>;
};

const pendingUpdates = new Map<string, PendingUpdate>();

const performUpdate = async (record: Pick<AssetClassRecord, 'id' | 'percentage'>): Promise<AssetClassRecord> => {
  const userId = await getUserId();
  const { data, error } = await supabase
    .from(TABLE)
    .update({ percentage: toPersistedPercentage(record.percentage) })
    .eq('id', record.id)
    .eq('user_id', userId)
    .select('id,code,percentage')
    .single();

  if (error) {
    throw error;
  }

  const mapped = mapRow(data);
  if (!mapped) {
    throw new Error('Registro de classe de ativo invalido');
  }

  return mapped;
};

const flushUpdate = async (id: string) => {
  const pending = pendingUpdates.get(id);
  if (!pending) return;

  pendingUpdates.delete(id);
  try {
    const result = await performUpdate(pending.record);
    pending.resolvers.forEach(({ resolve }) => resolve(result));
  } catch (error) {
    pending.resolvers.forEach(({ reject }) => reject(error));
  }
};

const scheduleUpdate = (record: Pick<AssetClassRecord, 'id' | 'percentage'>): Promise<AssetClassRecord> =>
  new Promise((resolve, reject) => {
    const existing = pendingUpdates.get(record.id);
    if (existing) {
      clearTimeout(existing.timer);
      existing.record = record;
      existing.resolvers.push({ resolve, reject });
      existing.timer = setTimeout(() => flushUpdate(record.id), UPDATE_DEBOUNCE_MS);
      return;
    }

    const timer = setTimeout(() => flushUpdate(record.id), UPDATE_DEBOUNCE_MS);
    pendingUpdates.set(record.id, { timer, record, resolvers: [{ resolve, reject }] });
  });

export const assetClassesService = {
  async list(): Promise<AssetClassRecord[]> {
    const userId = await getUserId();
    const { data, error } = await supabase.from(TABLE).select('id,code,percentage').eq('user_id', userId);
    if (error) {
      throw error;
    }

    return (data ?? []).map(mapRow).filter(Boolean) as AssetClassRecord[];
  },

  async create(input: { code: AssetClassCode; percentage: number }): Promise<AssetClassRecord> {
    const userId = await getUserId();
    const { data, error } = await supabase
      .from(TABLE)
      .insert({
        code: input.code,
        percentage: toPersistedPercentage(input.percentage),
        user_id: userId,
      })
      .select('id,code,percentage')
      .single();

    if (error) {
      throw error;
    }

    const mapped = mapRow(data);
    if (!mapped) {
      throw new Error('Registro de classe de ativo invalido');
    }

    return mapped;
  },

  update(record: Pick<AssetClassRecord, 'id' | 'percentage'>): Promise<AssetClassRecord> {
    // Debounce to avoid spamming updates when the slider changes quickly.
    return scheduleUpdate(record);
  },

  async remove(id: string): Promise<void> {
    const userId = await getUserId();
    const { error } = await supabase.from(TABLE).delete().eq('id', id).eq('user_id', userId);
    if (error) {
      throw error;
    }
  },
};
